import { ConfirmDialog } from "@/components/ConfirmDialog";
import { personName } from "@/components/boo/access-copy";
import { BooAvatars, DecidedLine, DepthTag, GrantStages, LinkStatePill, ScopeChips } from "@/components/grants/GrantBits";
import { ScopeEditor } from "@/components/grants/ScopeEditor";
import { Block, SheetShell } from "@/components/grants/SheetShell";
import { useInvalidateLink } from "@/components/grants/useGrantActions";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, api, type BooLinkRow, type GrantScopes, type Grantable } from "@/lib/api";
import { KIND_COPY, PRESETS, type ScopeKind, countIn, countLine, expirySoon, isEmpty, kindCounts, labelsOf, linkState, memoriesFrom, num, sameScopes, shortDate } from "@/lib/grant-scopes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Quote } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// What one Boo learns from another, from the learning side: what was asked,
// what the other Boo's admins granted, and what this Boo uses of it, next to
// what it has of its own. This Boo's admins pick within the grant, ask for
// more, or stop. Without `canManage` it only shows where things stand (the
// org page uses it that way for people who can't step in).
export function ConnectionSheet({
  containerId,
  link,
  canManage,
  note,
  onAskMore,
  onClose,
}: {
  /** The learning Boo. */
  containerId: string;
  link: BooLinkRow;
  canManage: boolean;
  /** A line under everything, for who can change it instead. */
  note?: string;
  onAskMore?: () => void;
  onClose: () => void;
}) {
  const source = link.source?.name ?? "the other Boo";
  const consumer = link.consumer?.name ?? "This Boo";
  const approved = link.status === "approved";
  const granted = link.granted;
  const grantable = useQuery({
    queryKey: ["grantable", "consumer", containerId, link.id],
    queryFn: () => api.containers.connections.grantable(containerId, link.id),
    enabled: approved && canManage && !!link.source?.running,
    retry: (n, e) => !(e instanceof ApiError && [403, 404, 409].includes(e.status)) && n < 2,
  });
  // What this Boo has of its own, to set beside what the other Boo shares.
  const own = useQuery({
    queryKey: ["grantable", "own", containerId],
    queryFn: () => api.containers.connections.grantable(containerId),
    enabled: approved && canManage && !!link.consumer?.running && !!link.source?.running,
    retry: (n, e) => !(e instanceof ApiError && [403, 404, 409].includes(e.status)) && n < 2,
    staleTime: 15_000,
  });
  const [pick, setPick] = useState<GrantScopes | null>(link.pick ?? granted);
  const [ending, setEnding] = useState(false);
  const invalidate = useInvalidateLink();

  const save = useMutation({
    mutationFn: () => api.containers.connections.pick(containerId, link.id, pick!),
    onSuccess: (res) => {
      toast.success(res.restarted ? `Saved. ${consumer} is using the new selection.` : `Saved. ${consumer} picks it up as soon as it finishes restarting.`);
      onClose();
    },
    onSettled: () => invalidate(link),
  });
  const retry = useMutation({
    mutationFn: () => api.containers.connections.retry(containerId, link.id),
    onSuccess: (res) =>
      res.result === "done"
        ? toast.success(`${consumer} is now learning from ${source}.`)
        : res.result === "waiting"
          ? toast.info(`It connects once both Boos are running.`)
          : toast.info("Already connecting."),
    onSettled: () => invalidate(link),
  });
  const withdraw = useMutation({
    mutationFn: () => api.containers.connections.withdrawMore(containerId, link.id),
    onSuccess: () => toast.success("Request canceled."),
    onSettled: () => invalidate(link),
  });
  const end = useMutation({
    mutationFn: () => api.containers.connections.end(containerId, link.id),
    onSuccess: () => {
      toast.success(link.status === "requested" ? "Request canceled." : `${consumer} no longer learns from ${source}.`);
      onClose();
    },
    onSettled: () => invalidate(link),
  });

  const g = grantable.data?.grantable;
  const busy = save.isPending || end.isPending || withdraw.isPending;
  const pickChanged = !!pick && !!link.pick && !sameScopes(pick, link.pick);
  const counts = g && pick && granted ? countIn(pick, g) : null;

  return (
    <SheetShell
      title={`What ${consumer} learns from ${source}`}
      onClose={onClose}
      busy={busy}
      footer={
        canManage && (
          <>
            {link.status !== "declined" && (
              <Button variant="ghost" className="mr-auto text-destructive hover:text-destructive" onClick={() => setEnding(true)} disabled={busy}>
                {link.status === "requested" ? "Cancel request" : "Stop learning"}
              </Button>
            )}
            {link.setUpFailed && (
              <Button variant="outline" onClick={() => retry.mutate()} loading={retry.isPending} disabled={busy}>
                Try again
              </Button>
            )}
            {approved && !link.more && !link.setUpFailed && onAskMore && (
              <Button variant="outline" onClick={onAskMore} disabled={busy}>
                Ask for more
              </Button>
            )}
            {link.status === "declined" && onAskMore && (
              <Button variant="outline" onClick={onAskMore}>
                Ask again
              </Button>
            )}
            {approved && link.setUp && (
              <Button onClick={() => save.mutate()} loading={save.isPending} disabled={busy || !pickChanged || !pick || isEmpty(pick)}>
                {counts ? `Use · ${countLine(counts, countIn(granted!, g!))}` : "Save"}
              </Button>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <BooAvatars consumer={link.consumer} source={link.source} />
          <LinkStatePill state={linkState(link)} />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {approved ? <DepthTag depth={link.grantedPreset} /> : <DepthTag depth={link.requestedPreset} />}
          {link.decidedAt ? <DecidedLine link={link} /> : link.requestedAt && <span>Asked by {personName(link.requestedBy)} · {shortDate(link.requestedAt)}</span>}
          {approved && link.sync && link.sync.state !== "ok" && (
            <span className="text-amber-800 dark:text-amber-300">
              Couldn't sync{link.sync.at ? ` since ${shortDate(link.sync.at)}` : ""}
            </span>
          )}
          {expirySoon(link.expiresAt) && <span className="text-amber-800 dark:text-amber-300">{expirySoon(link.expiresAt)}</span>}
        </div>
        {link.decisionNote && (
          <p className="flex gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm">
            <Quote className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span>
              {link.decisionNote}
              <span className="block text-xs text-muted-foreground">— {personName(link.decidedBy)}</span>
            </span>
          </p>
        )}
        {link.removalPendingSince && (
          <p className="text-xs text-sky-800 dark:text-sky-300">
            Stopping. {consumer} loses access within a few minutes.
          </p>
        )}
        <div className="border-t border-border pt-3">
          <GrantStages link={link} labels={labelsOf(g)} grantable={g} grantableBound={granted} />
        </div>
        {link.status === "requested" && link.requestNote && <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">“{link.requestNote}”</p>}
      </div>

      {link.more && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
          <span>
            Asked {source} for more on {shortDate(link.more.requestedAt)}: <ScopeChips scopes={link.more.scopes} className="mt-1" />
            {link.more.note && <span className="mt-1.5 block italic [overflow-wrap:anywhere]">“{link.more.note}”</span>}
          </span>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => withdraw.mutate()} loading={withdraw.isPending}>
              Cancel request
            </Button>
          )}
        </div>
      )}

      {g && own.data && (
        <SideBySide
          consumer={consumer}
          source={source}
          own={own.data.grantable}
          shared={g}
          older={{ own: own.data.scoped === false, shared: grantable.data?.scoped === false }}
        />
      )}

      {approved && granted && (
        <Block
          title={canManage ? "In use" : "Shared"}
        >
          {!canManage || !link.source?.running ? (
            <>
              <ScopeChips scopes={link.effective ?? granted} options={{ g }} />
              {!link.source?.running && canManage && <p className="text-xs text-muted-foreground">{source} isn't running.</p>}
            </>
          ) : grantable.isLoading ? (
            <Skeleton className="h-40 w-full rounded-lg" />
          ) : grantable.isError ? (
            <InlineError error={grantable.error} />
          ) : g && pick ? (
            <ScopeEditor grantable={g} bound={granted} value={pick} onChange={setPick} disabled={busy || !link.setUp} use />
          ) : null}
          {!link.setUp && <p className="text-xs text-muted-foreground">Connecting…</p>}
        </Block>
      )}

      {link.resharedFrom && link.resharedFrom.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Some of these memories came to {source} from {link.resharedFrom.join(", ")}.
        </p>
      )}

      {note && <p className="text-xs text-muted-foreground">{note}</p>}

      {(save.isError || end.isError || withdraw.isError || retry.isError) && <InlineError error={save.error ?? end.error ?? withdraw.error ?? retry.error} />}

      <ConfirmDialog
        open={ending}
        onOpenChange={setEnding}
        title={link.status === "requested" ? "Cancel this request?" : `Stop learning from ${source}?`}
        description={
          link.status === "requested" ? "The other Boo's admins won't be asked any more." : `${consumer} loses these skills and memories within a few minutes.`
        }
        confirmLabel={link.status === "requested" ? "Cancel request" : "Stop learning"}
        cancelLabel={link.status === "requested" ? "Keep request" : "Keep learning"}
        loading={end.isPending}
        onConfirm={() => end.mutate()}
      />
    </SheetShell>
  );
}

const KINDS: ScopeKind[] = ["skills", "org", "channels", "people"];

/** What the learning Boo has of its own next to what the other Boo shares with it, kind by kind. */
function SideBySide({
  consumer,
  source,
  own,
  shared,
  older,
}: {
  consumer: string;
  source: string;
  own: Grantable;
  shared: Grantable;
  /** A Boo on an older version reports skills and organization memories only. */
  older: { own: boolean; shared: boolean };
}) {
  const a = kindCounts(PRESETS.full, own);
  const b = kindCounts(PRESETS.full, shared);
  const cell = (c: typeof a, k: ScopeKind, isOlder: boolean) =>
    isOlder && (k === "channels" || k === "people")
      ? "—"
      : k === "skills"
      ? num(c.skills)
      : k === "org"
        ? num(c.org)
        : k === "channels"
          ? memoriesFrom(c.channelMemories, c.channels, "channel")
          : memoriesFrom(c.peopleMemories, c.people, "person", "people");
  return (
    <Block title="Side by side">
      <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] rounded-lg border border-border text-sm">
        <span className="border-b border-border px-3 py-2" />
        <span className="border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground [overflow-wrap:anywhere]">{consumer} has</span>
        <span className="border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground [overflow-wrap:anywhere]">{source} shares</span>
        {KINDS.map((k, i) => (
          <div key={k} className={cn("contents", i < KINDS.length - 1 && "[&>*]:border-b [&>*]:border-border")}>
            <span className="px-3 py-2 font-medium">{KIND_COPY[k].label}</span>
            <span className="px-3 py-2 tabular-nums text-muted-foreground">{cell(a, k, older.own)}</span>
            <span className="px-3 py-2 tabular-nums">{cell(b, k, older.shared)}</span>
          </div>
        ))}
      </div>
      {(older.own || older.shared) && <p className="text-xs text-muted-foreground">— Not reported by Boos on an older version.</p>}
    </Block>
  );
}
